<template>
  <div>
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderPage :page="currentPageInfo" />
    <div :class="$style.container">
      <div class="order">
        <section
          v-if="metaContent.value?.description"
          class="section order-last"
        >
          <div
            class="description"
            v-html="metaContent.value.description"
          />
        </section>
        <div class="order-first">
          <SearchResults :with-search="false">
            <template #title>
              <h2 class="title-strat">
                {{
                  currentContests?.length
                    ? 'Les concours du moment'
                    : 'Revenez vite pour retrouver de nouveaux concours !'
                }}
              </h2>
            </template>
            <template #results>
              <ContestContainer :items="currentContests" />
            </template>
          </SearchResults>
          <SearchResults
            v-if="pastContests?.length"
            :with-search="false"
          >
            <template #title>
              <h2 class="title-strat">Résultats des anciens concours</h2>
            </template>
            <template #results>
              <ContestContainer
                :items="pastContests"
                no-result-text="Aucun concours disponible"
                past
              />
            </template>
          </SearchResults>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isBefore } from 'date-fns'

definePageMeta({
  middleware: async (to) => {
    const loadContests = useLoadContests()
    await loadContests(to)
  },
})

const route = useRoute()
const contestsStore = useContestsStore()
const metaContentStore = useMetaContentStore()

const arianeRoute = [
  {
    title: 'Tous les concours',
    route: '',
  },
]

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: 'Découvrez les concours We Are PlayStation',
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: 'Toutes les concours We Are PlayStation',
  description: 'Découvrez tous les concours We Are PlayStation.',
  ogTitle: 'Toutes les concours We Are PlayStation',
  ogDescription: 'Découvrez tous les concours We Are PlayStation.',
  ogImage: 'https://www.weareplaystation.fr/img/tous-les-concours.jpg',
})
const currentPageInfo = {
  title: 'Tous les concours',
  image: '/img/tous-les-concours.jpg',
  titleSmall: 'tous les',
  titleBig: 'concours',
  description: 'Découvrez tous les concours We Are PlayStation.',
}

const metaContent = computed(() => {
  return metaContentStore.getTransverseBySlug(route.name)
})

const currentContests = computed(() => {
  if (!contestsStore.contests) return []

  const now = new Date()
  return contestsStore.contests
    .filter((contest) =>
      isBefore(now, new Date(contest.displayed_ends_at || contest.contest_end)),
    )
    .sort((a, b) => {
      const aEnd = new Date(a.displayed_ends_at || a.contest_end).getTime()
      const bEnd = new Date(b.displayed_ends_at || b.contest_end).getTime()

      return aEnd - bEnd
    })
})

const pastContests = computed(() => {
  if (!contestsStore.contests) return []

  // Must only contains "new" contest type (a.k.a photo/video)
  // which ones have a `contest_end_display` prop

  const now = new Date()
  return contestsStore.contests
    .filter(
      (contest) =>
        contest.contest_end_display &&
        isBefore(new Date(contest.contest_end), now),
    )
    .sort((a, b) => {
      const aEnd = new Date(a.contest_end).getTime()
      const bEnd = new Date(b.contest_end).getTime()

      return bEnd - aEnd
    })
})
</script>

<style lang="scss" module>
.container {
  margin-bottom: 8rem;
}
</style>

<style lang="scss" scoped>
.order {
  display: flex;
  flex-direction: column;

  &-first {
    order: 1;
  }

  &-last {
    order: 2;
    width: 100%;
  }

  .description {
    &:deep(h2) {
      margin-bottom: 1.5rem;
    }

    &:deep(h3) {
      margin-bottom: 1rem;
      font-family: SST;
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 2rem;
      letter-spacing: 0.225rem;
      text-transform: uppercase;
    }

    &:deep(p) {
      margin-bottom: 1rem;
    }

    &:deep(ul) {
      margin-bottom: 1rem;
      padding-left: 1em;
      list-style: disc;
    }
  }
}
</style>
